import { useState } from "react";
import {
  useCompanySuggestions,
  useApproveCompanySuggestion,
  useRejectCompanySuggestion,
} from "@/hooks/useCompanySuggestions";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export function CompanySuggestionsPage() {
  useDocumentTitle("Company Suggestions");
  const { data: suggestions, isLoading, error } = useCompanySuggestions();
  const approveMutation = useApproveCompanySuggestion();
  const rejectMutation = useRejectCompanySuggestion();
  const [actionId, setActionId] = useState<string | null>(null);
  const [country, setCountry] = useState("IN");
  const [reason, setReason] = useState("");

  function handleApprove(id: string) {
    approveMutation.mutate(
      { id, country },
      { onSuccess: () => { setActionId(null); setCountry("IN"); } },
    );
  }

  function handleReject(id: string) {
    rejectMutation.mutate(
      { id, reason: reason || "Rejected by admin" },
      { onSuccess: () => { setActionId(null); setReason(""); } },
    );
  }

  return (
    <div>
      <h1 className="font-headline-lg text-headline-lg text-on-surface mb-stack-md">
        Company Suggestions
      </h1>

      {isLoading && (
        <p className="font-body-md text-on-surface-variant">Loading suggestions...</p>
      )}

      {error && (
        <p className="font-body-md text-error">Failed to load suggestions.</p>
      )}

      {suggestions && suggestions.length === 0 && (
        <p className="font-body-md text-on-surface-variant">No pending suggestions.</p>
      )}

      {suggestions && suggestions.length > 0 && (
        <div className="space-y-stack-sm">
          {suggestions.map((s) => (
            <div
              key={s.id}
              className="bg-surface-container-lowest border border-white/5 rounded-xl p-stack-md"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-body-md text-on-surface font-medium">{s.name}</p>
                  <p className="font-label-xs text-on-surface-variant">
                    {new Date(s.createdAt).toLocaleDateString()}
                  </p>
                </div>

                {actionId !== s.id ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setActionId(s.id)}
                      className="px-3 py-1 bg-primary-container text-on-primary-container rounded-lg font-label-sm text-label-sm hover:opacity-90"
                    >
                      Review
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 items-end">
                    <div className="flex gap-2">
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="bg-surface-container-high text-on-surface font-body-md rounded-lg px-2 py-1 text-sm border border-outline-variant"
                      >
                        <option value="IN">India</option>
                        <option value="US">United States</option>
                        <option value="UNKNOWN">Unknown</option>
                      </select>
                      <button
                        onClick={() => handleApprove(s.id)}
                        disabled={approveMutation.isPending}
                        className="px-3 py-1 bg-primary-container text-on-primary-container rounded-lg font-label-sm text-label-sm hover:opacity-90 disabled:opacity-50"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(s.id)}
                        disabled={rejectMutation.isPending}
                        className="px-3 py-1 bg-error-container text-on-error-container rounded-lg font-label-sm text-label-sm hover:opacity-90 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => setActionId(null)}
                        className="px-3 py-1 text-on-surface-variant font-label-sm text-label-sm hover:text-on-surface"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
